import { Query } from '@diag-ui/chart'

export type Schema = {
  queries: Query[]
  unit: string
  title: string
  subQueries?: Omit<Schema, 'title'>
}
export type Schemas = Schema[]
export type SegmentSchemas = { title: string; schemas: Schemas }[]

const totalQPSSchema: Schema = {
  title: 'QPS',
  unit: 'short',
  queries: [
    {
      name: '{type}',
      promql: `sum(rate(tidb_executor_statement_total{k8s_cluster="", {label_cluster_id}}[2m])) by (type)`,
      type: 'line'
    },
    {
      name: 'Total',
      promql: `sum(rate(tidb_executor_statement_total{k8s_cluster="", {label_cluster_id}}[2m]))`,
      type: 'line'
    },
    {
      name: 'Failed',
      promql: `sum(rate(tidb_server_execute_error_total{k8s_cluster="", {label_cluster_id}}[2m]))`,
      type: 'line'
    }
  ]
}

const instanceQPSSchema: Schema = {
  title: 'QPS',
  queries: [
    {
      promql: `sum(rate(tidb_executor_statement_total{{label_cluster_id}, {label_instance_name}}[2m])) by (instance)`,
      name: '{instance}',
      type: 'line'
    }
  ],
  unit: 'short'
}

const queryDurationSchema: Schema = {
  title: 'Query Duration',
  queries: [
    {
      promql: `sum(rate(tidb_server_handle_query_duration_seconds_sum{{label_cluster_id}, {label_instance_name},sql_type!="internal"}[2m]))by(instance)/sum(rate(tidb_server_handle_query_duration_seconds_count{{label_cluster_id}, {label_instance_name},sql_type!="internal"}[2m]))by(instance)`,
      name: '{instance}',
      type: 'line'
    }
  ],
  unit: 's'
}

const connectionCountSchema: Schema = {
  title: 'Connection Count',
  queries: [
    {
      promql: `sum(tidb_server_tokens{{label_cluster_id}, {label_instance_name}}) by (instance)`,
      name: '{instance}',
      type: 'line'
    }
  ],
  unit: 'short'
}

const allUptimeSchema: Schema = {
  title: 'Up Time',
  queries: [
    {
      promql: `time()-process_start_time_seconds{{label_cluster_id},{label_component}="tidb"}`,
      name: '{instance} - TiDB',
      type: 'line'
    },
    {
      promql: `time()-process_start_time_seconds{{label_cluster_id},{label_component}="tikv"}`,
      name: '{instance} - TiKV',
      type: 'line'
    },
    {
      promql: `time()-process_start_time_seconds{{label_cluster_id},{label_component}="pd"}`,
      name: '{instance} - PD',
      type: 'line'
    },
    {
      promql: `tiflash_system_asynchronous_metric_Uptime{{label_cluster_id}}`,
      name: '{instance} - TiFlash',
      type: 'line'
    }
  ],
  unit: 's'
}

const tikvUpTimeSchema: Schema = {
  title: 'TiKV Up Time',
  queries: [
    {
      promql: `time()-process_start_time_seconds{{label_cluster_id},{label_instance_name},{label_component}="tikv"}`,
      name: '{instance} - TiKV',
      type: 'line'
    }
  ],
  unit: 's'
}

const tikvMemorySchema: Schema = {
  title: 'TiKV Memory',
  queries: [
    {
      promql: `avg(process_resident_memory_bytes{{label_cluster_id}, {label_instance_name},{label_component}="tikv"})`,
      name: '{instance}',
      type: 'line'
    }
  ],
  unit: 'bytes'
}

const defaultSchemas: Schemas = [
  instanceQPSSchema,
  queryDurationSchema,
  connectionCountSchema,
  allUptimeSchema
]

const tikvOOMSchemas: Schemas = [
  {
    title: 'Allocator Stats',
    queries: [
      {
        promql: `tikv_allocator_stats{{label_cluster_id},{label_instance_name}}`,
        name: '{type}',
        type: 'line'
      }
    ],
    unit: 'bytes'
  },
  {
    title: 'Rocksdb Block Cache',
    queries: [
      {
        promql: `topk(20, avg(tikv_engine_block_cache_size_bytes{{label_cluster_id},{label_instance_name}}) by (cf, instance))`,
        name: '{instance}-{cf}',
        type: 'line'
      }
    ],
    unit: 'bytes'
  },
  {
    title: 'Rocksdb KV Memtable',
    queries: [
      {
        promql: `avg(tikv_engine_memory_bytes{{label_cluster_id},{label_instance_name}, type="mem-tables"}) by (cf)`,
        name: '{cf}',
        type: 'line'
      }
    ],
    unit: 'bytes'
  },
  {
    title: 'Lock Heap Size',
    queries: [
      {
        promql: `avg(tikv_resolved_ts_lock_heap_bytes{{label_cluster_id},{label_instance_name}}) by (instance)`,
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'bytes'
  },
  tikvMemorySchema,
  {
    title: 'Region',
    queries: [
      {
        promql: `sum(tikv_raftstore_region_count{{label_cluster_id},{label_instance_name}, type="region"}) by (instance)`,
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'short',
    subQueries: {
      queries: [
        {
          promql: `sum(tikv_raftstore_region_count{{label_cluster_id},{label_instance_name}, type="region"}) by (instance) / 20`,
          name: 'Estimate Memory Usage - {instance}',
          type: 'line'
        }
      ],
      unit: 'mbytes'
    }
  }
]

const tikvOOMCopSchemas: Schemas = [
  {
    title: 'Read Pool CPU',
    queries: [
      {
        promql: `sum(rate(tikv_thread_cpu_seconds_total{{label_cluster_id},{label_instance_name}, name=~"unified_read_po.*"}[2m])) by (instance)`,
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'percent'
  },
  {
    title: 'Running Tasks',
    queries: [
      {
        promql:
          'sum(avg_over_time(tikv_unified_read_pool_running_tasks{{label_cluster_id},{label_instance_name}}[1m])) by (instance)',
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'short'
  },
  {
    title: 'Total Requests',
    queries: [
      {
        promql:
          'sum(rate(tikv_coprocessor_request_duration_seconds_count{{label_cluster_id},{label_instance_name}}[2m])) by (req)',
        name: '{req}',
        type: 'line'
      }
    ],
    unit: 'ops'
  },
  {
    title: 'Total KV Cursor Operations',
    queries: [
      {
        promql:
          'sum(rate(tikv_coprocessor_scan_keys_sum{{label_cluster_id},{label_instance_name}}[2m])) by (req)',
        name: '{req}',
        type: 'line'
      }
    ],
    unit: 'short'
  }
]

const brSchemas: Schemas = [
  instanceQPSSchema,
  queryDurationSchema,
  {
    title: 'TiKV CPU',
    queries: [
      {
        promql: `sum(rate(tikv_thread_cpu_seconds_total{{label_cluster_id}, {label_instance_name}}[2m]))by(instance)*100`,
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'percent'
  },
  {
    title: 'TiKV Memory',
    queries: [
      {
        promql: `process_resident_memory_bytes{{label_cluster_id}, {label_instance_name},{label_component}="tikv"}`,
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'bytes'
  }
]

const oomRiskSchemas: Schemas = [
  instanceQPSSchema,
  queryDurationSchema,
  {
    title: 'TiDB Uptime',
    unit: 's',
    queries: [
      {
        name: '{instance}',
        promql: `(time() - process_start_time_seconds{k8s_cluster="", {label_cluster_id}, {label_instance_name}, {label_component}="tidb"})`,
        type: 'line'
      }
    ]
  },
  {
    title: 'TiDB Memory',
    unit: 'bytes',
    queries: [
      {
        name: '{instance}',
        promql: `process_resident_memory_bytes{{label_cluster_id}, {label_instance_name}, {label_component}="tidb"}`,
        type: 'line'
      }
    ]
  },
  {
    title: 'TiDB CPU',
    unit: 'percent',
    queries: [
      {
        name: '{instance}',
        promql: `rate(process_cpu_seconds_total{{label_cluster_id}, {label_instance_name},{label_component}="tidb"}[2m])*100`,
        type: 'line'
      }
    ]
  }
]

const rebootSchemas: Schemas = [
  {
    title: 'Instance Memory',
    unit: 'bytes',
    queries: [
      {
        name: 'alloc-from-os',
        promql: `process_resident_memory_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}}`,
        type: 'line'
      },
      {
        name: 'estimate-inuse',
        promql: `go_memstats_next_gc_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}} / (1 + tidb_server_gogc{k8s_cluster="", {label_cluster_id}, {label_instance_name}} / 100)`,
        type: 'line'
      },
      {
        name: 'estimate-garbage',
        promql: `go_memstats_heap_alloc_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}} - go_memstats_next_gc_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}} / (1 + tidb_server_gogc{k8s_cluster="", {label_cluster_id}, {label_instance_name}} / 100)`,
        type: 'line'
      },
      {
        name: 'reserved-by-go',
        promql: `go_memstats_heap_idle_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}} - go_memstats_heap_released_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}} + go_memstats_heap_inuse_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}} - go_memstats_heap_alloc_bytes{k8s_cluster="", {label_cluster_id}, {label_instance_name}}`,
        type: 'line'
      }
    ]
  },
  {
    title: 'TiDB Uptime',
    unit: 's',
    queries: [
      {
        name: '{instance}',
        promql: `(time() - process_start_time_seconds{k8s_cluster="", {label_cluster_id}, {label_instance_name}, {label_component}="tidb"})`,
        type: 'line'
      }
    ]
  },
  {
    title: 'QPS',
    unit: 'short',
    queries: [
      {
        name: '{type}',
        promql: `sum(rate(tidb_executor_statement_total{k8s_cluster="", {label_cluster_id}}[2m])) by (type)`,
        type: 'line'
      },
      {
        name: 'Total',
        promql: `sum(rate(tidb_executor_statement_total{k8s_cluster="", {label_cluster_id}}[2m]))`,
        type: 'line'
      },
      {
        name: 'Failed',
        promql: `sum(rate(tidb_server_execute_error_total{k8s_cluster="", {label_cluster_id}}[2m]))`,
        type: 'line'
      }
    ]
  }
]

const tikvRebootSchemas: Schemas = [
  tikvUpTimeSchema,
  tikvMemorySchema,
  totalQPSSchema
]

const upgradeSchemas: Schemas = [
  instanceQPSSchema,
  queryDurationSchema,
  connectionCountSchema,
  allUptimeSchema,
  {
    title: 'Leader for TiKV',
    queries: [
      {
        promql: `sum(tikv_raftstore_region_count{k8s_cluster="", {label_cluster_id}, type="leader"}) by (instance)`,
        name: '{instance}',
        type: 'line'
      }
    ],
    unit: 'short'
  }
]

export const schemasMap: {
  [k: string]: Schemas | SegmentSchemas
} = {
  default: defaultSchemas,
  restore: brSchemas,
  import: brSchemas,
  backup: brSchemas,
  upgrade: upgradeSchemas,
  'oom risk': oomRiskSchemas,
  'tikv oom': [
    { title: 'TiKV', schemas: tikvOOMSchemas },
    { title: 'Coprocessor', schemas: tikvOOMCopSchemas }
  ],
  reboot: rebootSchemas,
  'tidb reboot': rebootSchemas,
  'tikv reboot': tikvRebootSchemas
}

export const isSegmentSchemas = (
  s: Schemas | SegmentSchemas
): s is SegmentSchemas => {
  return !!(s as SegmentSchemas)[0].schemas
}
